/**
 * AWARE product branding — canonical customer-facing naming.
 *
 * Feb 2026 — introduces the AWARE 4-Point / AWARE 360 product family.
 * Single source of truth for every customer-facing product string on
 * the marketing site, booking flow, admin surfaces and portal.
 *
 * The backend equivalent lives at `/app/backend/product_branding.py`
 * — the two MUST stay in sync (a lint check in the test catalog
 * regression compares the two lists).
 *
 * Internal test IDs (`basic` | `tank` | `bore`) are unchanged. Only
 * the display strings differ.
 */

export const PRODUCT_BRAND = {
  basic: {
    displayName:   "AWARE 4-Point",
    shortCode:     "4-POINT",
    family:        "4-Point",
    description:   "Essential Water Safety Check",
    marketingLine: "Four key indicators. One clear water check.",
    badgeStyle:    "four-point",
    legacyName:    "Basic Water Safety Check",
  },
  tank: {
    displayName:   "AWARE Tank 360",
    shortCode:     "TANK 360",
    family:        "360",
    description:   "Comprehensive Tank Water Analysis",
    marketingLine: "A complete view of your tank water.",
    badgeStyle:    "tank-360",
    legacyName:    "Essential Tank Water Test",
  },
  bore: {
    displayName:   "AWARE Bore 360",
    shortCode:     "BORE 360",
    family:        "360",
    description:   "Comprehensive Bore Water Analysis",
    marketingLine: "A complete view of your bore water.",
    badgeStyle:    "bore-360",
    legacyName:    "Essential Bore Water Test",
  },
};

/**
 * Return the branding record for a given internal test id.
 * Falls back to a neutral "AWARE Water Test" record when the id is
 * unknown so callers never crash on legacy or malformed data.
 */
export function getProductBrand(testId) {
  if (testId && PRODUCT_BRAND[testId]) return PRODUCT_BRAND[testId];
  return {
    displayName:   "AWARE Water Test",
    shortCode:     "AWARE",
    family:        "AWARE",
    description:   "Aware Water Testing Analysis",
    marketingLine: "",
    badgeStyle:    "generic",
    legacyName:    "",
  };
}

/** Convenience helper — returns just the display name string. */
export function displayNameFor(testId) {
  return getProductBrand(testId).displayName;
}

/** Reverse-lookup: retired legacy name → new canonical display name. */
export const LEGACY_NAME_MAP = Object.values(PRODUCT_BRAND).reduce((acc, b) => {
  if (b.legacyName) acc[b.legacyName] = b.displayName;
  return acc;
}, {});

/**
 * Normalise historical strings from the DB back onto the new canonical
 * names. Passes non-legacy strings through unchanged. Safe on null.
 */
export function normaliseLegacyName(raw) {
  if (!raw) return raw;
  return LEGACY_NAME_MAP[String(raw).trim()] || raw;
}
